/**
 * @file Вид каталога.
 * Отвечает за отрисовку DOM:
 * - видимость и порядок элементов в списке;
 * - счётчик результатов;
 * - пагинацию;
 * - активную кнопку сортировки.
 */

/**
 * @class
 */
class CatalogView {
  /**
   * @param {object} params
   * @param {Element} params.root - Корневой элемент Stimulus-контроллера.
   * @param {Element|null} params.list - Контейнер списка элементов.
   * @param {Element[]} params.items - DOM-узлы всех элементов каталога.
   * @param {Element|null} params.resultCount - Элемент для отображения количества результатов.
   * @param {Element|null} params.pagination - Контейнер пагинации.
   * @param {Element|null} params.emptyState - Блок «ничего не найдено».
   * @param {Element[]} [params.hideOnEmpty] - Элементы, скрываемые при пустом результате
   *   (сортировка, свитч карты).
   */
  constructor({
    root,
    list,
    items,
    resultCount,
    pagination,
    emptyState,
    hideOnEmpty
  }) {
    this.root = root
    this.list = list
    this.nodesById = new Map(
      items.map((node) => [String(node.dataset.id), node])
    )
    this.resultCount = resultCount
    this.resultCountTemplates = this.#resultCountTemplates()
    this.resultCountPluralRules = this.#resultCountPluralRules()
    this.pagination = pagination
    this.emptyState = emptyState
    this.hideOnEmpty = hideOnEmpty || []

    this.#preparePagination()
  }

  /**
   * Обновляет DOM по заданному снимку: список элементов, счётчик, пагинацию и сортировку.
   * @param {{ orderedIds: string[], pageIds: string[], page: number, pageSize: number, totalItems: number, totalPages: number }} snapshot
   * @param {string} sort - Ключ активной сортировки.
   */
  render(snapshot, sort) {
    this.#renderItems(snapshot)
    this.#renderEmptyState(snapshot)
    this.#renderResultCount(snapshot)
    this.#renderPagination(snapshot)
    this.#syncSortControls(sort)
  }

  /**
   * Проверяет, принадлежит ли кнопка блоку пагинации этого вида.
   * @param {Element} button
   * @returns {boolean}
   */
  containsPaginationButton(button) {
    return this.pagination?.contains(button) || false
  }

  #renderItems(snapshot) {
    if (!this.list) return

    const visibleIds = new Set(snapshot.pageIds.map(String))
    const orderedNodes = document.createDocumentFragment()

    this.nodesById.forEach((node) => {
      node.hidden = true
    })

    snapshot.orderedIds.forEach((id) => {
      const node = this.nodesById.get(String(id))
      if (!node) return

      node.hidden = !visibleIds.has(String(id))
      orderedNodes.append(node)
    })

    this.list.append(orderedNodes)
  }

  /**
   * Показывает блок «ничего не найдено», когда фильтры не оставили ни одного элемента,
   * и скрывает элементы, бессмысленные без результатов (сортировка, свитч карты).
   */
  #renderEmptyState(snapshot) {
    if (!this.emptyState) return

    const isEmpty = snapshot.totalItems === 0
    this.emptyState.hidden = !isEmpty
    if (this.list) this.list.hidden = isEmpty
    this.hideOnEmpty.forEach((element) => {
      element.hidden = isEmpty
    })
  }

  #renderResultCount(snapshot) {
    if (!this.resultCount) return

    const count = snapshot.totalItems
    const form = this.resultCountPluralRules?.select(count)
    const template =
      this.resultCountTemplates[form] ||
      this.resultCountTemplates.other ||
      this.resultCountTemplates.base

    // Без шаблонов сохраняем прежнее поведение, а при наличии форм обновляем
    // всю фразу: иначе после фильтрации число и склонение могли бы разойтись.
    this.resultCount.textContent = template
      ? template.replaceAll('%{count}', String(count))
      : count
  }

  #resultCountTemplates() {
    if (!this.resultCount?.dataset.countTemplates) return {}

    try {
      return JSON.parse(this.resultCount.dataset.countTemplates)
    } catch {
      return {}
    }
  }

  #resultCountPluralRules() {
    if (
      !this.resultCount ||
      typeof Intl === 'undefined' ||
      typeof Intl.PluralRules !== 'function'
    )
      return null

    try {
      return new Intl.PluralRules(
        this.resultCount.dataset.countLocale || document.documentElement.lang
      )
    } catch {
      return null
    }
  }

  #preparePagination() {
    if (!this.pagination) return

    const prevItem =
      this.pagination.querySelector('.pagination__prev')?.parentElement
    const nextItem =
      this.pagination.querySelector('.pagination__next')?.parentElement

    this.paginationPrevTemplate = prevItem?.cloneNode(true)
    this.paginationNextTemplate = nextItem?.cloneNode(true)

    this.pagination.querySelectorAll('[data-action]').forEach((element) => {
      element.removeAttribute('data-action')
    })
  }

  #renderPagination(snapshot) {
    if (!this.pagination) return

    const list = this.pagination.querySelector('.pagination__list')
    const showMore = this.pagination.querySelector('.pagination__show-more')

    this.pagination.hidden = snapshot.totalItems <= snapshot.pageSize
    if (showMore) showMore.hidden = snapshot.pageSize >= snapshot.totalItems
    if (!list) return

    const items = []
    const prevItem = this.#paginationDirectionItem(
      this.paginationPrevTemplate,
      snapshot.page === 1
    )

    if (prevItem) items.push(prevItem)

    this.#paginationPages(snapshot.page, snapshot.totalPages).forEach(
      (page) => {
        items.push(this.#paginationPageItem(page, snapshot.page))
      }
    )

    const nextItem = this.#paginationDirectionItem(
      this.paginationNextTemplate,
      snapshot.page === snapshot.totalPages
    )

    if (nextItem) items.push(nextItem)
    list.replaceChildren(...items)
  }

  #paginationDirectionItem(template, hidden) {
    if (!template || hidden) return null

    const item = template.cloneNode(true)
    const button = item.querySelector('button')

    if (button) {
      button.removeAttribute('data-action')
    }

    return item
  }

  #paginationPageItem(page, currentPage) {
    const item = document.createElement('li')
    item.className = 'pagination__item'

    if (page === 'ellipsis') {
      const ellipsis = document.createElement('span')
      ellipsis.className = 'pagination__ellipsis'
      ellipsis.setAttribute('aria-hidden', 'true')
      ellipsis.textContent = '…'
      item.append(ellipsis)
      return item
    }

    if (page === currentPage) {
      const current = document.createElement('span')
      current.className = 'pagination__page is-current'
      current.setAttribute('aria-current', 'page')
      current.textContent = page
      item.append(current)
      return item
    }

    const button = document.createElement('button')
    button.className = 'pagination__page'
    button.type = 'button'
    button.dataset.page = page
    button.textContent = page
    item.append(button)

    return item
  }

  #paginationPages(currentPage, totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }

    const pages = new Set([
      1,
      totalPages,
      currentPage - 1,
      currentPage,
      currentPage + 1
    ])
    const visiblePages = [...pages]
      .filter((page) => page > 0 && page <= totalPages)
      .sort((a, b) => a - b)

    return visiblePages.flatMap((page, index) => {
      const previousPage = visiblePages[index - 1]

      return previousPage && page - previousPage > 1
        ? ['ellipsis', page]
        : [page]
    })
  }

  #syncSortControls(sort) {
    const options = this.root.querySelectorAll('[data-sort]')
    const selectedOption = [...options].find(
      (option) => option.dataset.sort === sort
    )

    options.forEach((option) => {
      option.classList.toggle('is-active', option === selectedOption)
    })

    const dropdown = selectedOption?.closest('.dropdown')
    const buttonText = dropdown?.querySelector(
      '[data-dropdown-target="btn"] span'
    )

    if (buttonText) buttonText.textContent = selectedOption.textContent.trim()
  }
}

export default CatalogView
